/**
 * Gets the LearnerAssignment resource from the server, instantiates all the objects that it represents, and returns
 * those objects. The returned objects are
 * {
 *   submissionLearnerSchema: MultipartSubmissionLearnerSchema,
 *   versionId: Opaque Version Id
 * }
 */
import _ from 'lodash';

import { getIetfLanguageTag } from 'js/lib/language';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import type { CmlContent } from 'bundles/cml';
import FilesSubmissionBuilderSchema from 'bundles/programming/models/filesSubmissionBuilderSchema';
import Instructions from 'bundles/programming/models/instructions';
import MultipartSubmissionLearnerSchema from 'bundles/programming/models/multipartSubmissionLearnerSchema';
import ScriptSubmissionBuilderSchema from 'bundles/programming/models/scriptSubmissionBuilderSchema';
import WorkspaceSubmissionBuilderSchema from 'bundles/programming/models/workspaceSubmissionBuilderSchema';

// Backbone models
export type SubmissionBuilderSchemaModel =
  | typeof FilesSubmissionBuilderSchema
  | typeof ScriptSubmissionBuilderSchema
  | typeof WorkspaceSubmissionBuilderSchema;

export type SubmissionLearnerSchemaModel = typeof MultipartSubmissionLearnerSchema;

export const SubmissionBuilderSchemaTypeNames = {
  WEB: 'files',
  SCRIPT: 'script',
  LAB: 'workspace',
} as const;

export type SubmissionBuilderSchemaTypeNamesKeys = keyof typeof SubmissionBuilderSchemaTypeNames;

export type SubmissionBuilderSchemaTypeName =
  (typeof SubmissionBuilderSchemaTypeNames)[SubmissionBuilderSchemaTypeNamesKeys];

// Response before switching schemas to models
export type LearnerAssignmentData = {
  courseId: string;
  id: string;
  itemId: string;
  maxScore: number;
  passingFraction: number;
  passingScore: number;
  submissionBuilderSchema: {
    typeName: SubmissionBuilderSchemaTypeName;
    definition: {
      suggestedFilenames: {
        [partId: string]: string;
      };
    };
  };
  submissionLearnerSchema: {
    typeName: 'multipart';
    definition: {
      assignmentInstructions: CmlContent;
      parts: {
        [partId: string]: {
          maxScore: number;
          order: number;
          title: string;
        };
      };
    };
  };
  versionId: string;
};

// Type for promise factory below - converts the two schemas into Backbone models with make*
export type LearnerAssignment = {
  maxScore: number;
  passingFraction: number;
  passingScore: number;
  submissionBuilderSchema: SubmissionBuilderSchemaModel;
  submissionLearnerSchema: SubmissionLearnerSchemaModel;
  versionId: string;
};

function makeSubmissionLearnerSchema(
  learnerAssignmentData: LearnerAssignmentData
): SubmissionLearnerSchemaModel | null {
  const submissionLearnerSchemaData = learnerAssignmentData.submissionLearnerSchema;

  if (submissionLearnerSchemaData.typeName === 'multipart') {
    const { definition } = submissionLearnerSchemaData;

    return new MultipartSubmissionLearnerSchema({
      instructions: new Instructions(_.pick(definition, 'assignmentInstructions')),
      parts: definition.parts,
    });
  } else {
    throw new Error('Cannot construct ' + submissionLearnerSchemaData.typeName + ' SubmissionLearnerSchema.');
  }
}

function makeSubmissionBuilderSchema(
  learnerAssignmentData: LearnerAssignmentData
): SubmissionBuilderSchemaModel | null {
  const submissionBuilderSchemaData = learnerAssignmentData.submissionBuilderSchema;
  const { typeName, definition } = submissionBuilderSchemaData;

  if (typeName === SubmissionBuilderSchemaTypeNames.WEB) {
    return new FilesSubmissionBuilderSchema({
      suggestedFilenames: definition.suggestedFilenames,
    });
  } else if (typeName === SubmissionBuilderSchemaTypeNames.SCRIPT) {
    return new ScriptSubmissionBuilderSchema();
  } else if (typeName === SubmissionBuilderSchemaTypeNames.LAB) {
    return new WorkspaceSubmissionBuilderSchema();
  } else {
    throw new Error('Cannot construct ' + typeName + ' SubmissionBuilderSchema.');
  }
}

export const learnerAssignmentDataToLearnerAssignment = (
  learnerAssignmentData: LearnerAssignmentData
): LearnerAssignment => {
  const { maxScore, passingFraction, passingScore, versionId } = learnerAssignmentData;

  return {
    maxScore,
    passingFraction,
    passingScore,
    submissionBuilderSchema: makeSubmissionBuilderSchema(learnerAssignmentData),
    submissionLearnerSchema: makeSubmissionLearnerSchema(learnerAssignmentData),
    versionId,
  };
};

export const getLearnerAssignment = async ({
  itemId,
  courseId,
}: {
  itemId: string;
  courseId: string;
}): Promise<LearnerAssignment> => {
  const id = tupleToStringKey([courseId, itemId]);
  const fieldsString = [
    'maxScore',
    'passingFraction',
    'passingScore',
    'submissionLearnerSchema',
    'submissionBuilderSchema',
    'versionId',
  ].join(',');
  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Accept-Language', getIetfLanguageTag());
  const response = (await (
    await fetch(`/api/onDemandProgrammingLearnerAssignments.v1/${id}?fields=${fieldsString}`, {
      headers: requestHeaders,
    })
  ).json()) as { elements: Array<LearnerAssignmentData> };
  const learnerAssignmentData = response.elements[0];

  return learnerAssignmentDataToLearnerAssignment(learnerAssignmentData);
};

export default getLearnerAssignment;
